import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  DataSource, DataSourceService, Feature, featureToOl, GeoWorkspaceOptions, IgoMap,
  LayerService, QueryableDataSourceOptions, VectorLayer
} from '@igo2/geo';
import { debounceTime, Subscription, take } from 'rxjs';
import { ValeurDomaineDto } from 'src/app/shared/models/domaines.dto';
import { DomainesService } from 'src/app/shared/services/domaines.service';
import { ZzzConfigService } from 'src/app/zzz/core/config/config.service';
import { MapState, WorkspaceState } from '@igo2/integration';
import { Constantes } from 'src/app/shared/constantes';
import { ToolComponent } from '@igo2/common/tool';
import { EntityTableButton, EntityTableColumnRenderer, EntityTableTemplate } from '@igo2/common/entity';
import { MessageService } from '@igo2/core/message';
import { LanguageService } from '@igo2/core/language';
import { StorageService } from '@igo2/core/storage';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatGridListModule } from '@angular/material/grid-list';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import * as olstyle from 'ol/style';
import olClusterSource from 'ol/source/Cluster';
import olVectorSource from 'ol/source/Vector';
import { MatInputModule } from '@angular/material/input';

export type NameOfCommentaireLayer = 'CommentairesPlansTravailPoints' | 'CommentairesPlansOfficielsPoints' | 'Consultations';

@ToolComponent({
  name: 'sigt-rapport-commentaire',
  title: 'igo.integration.tools.sigtRapportCommentaire',
  icon: 'docs'
})
@Component({
    selector: 'app-sigt-rapport-commentaire',
    templateUrl: './sigt-rapport-commentaire.component.html',
    styleUrls: ['./sigt-rapport-commentaire.component.scss'],
    imports: [
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatDatepickerModule,
        MatButtonModule,
        MatGridListModule,
        CommonModule,
        ReactiveFormsModule,
        TranslateModule
    ]
})
export class SigtRapportCommentaireComponent implements OnInit {
  languageService = inject(LanguageService);

  private baseUrl: string;
  form: UntypedFormGroup;
  listeTypeDePlan: ValeurDomaineDto[];
  valeurTypePlanDefaut: string;
  listeRegion: ValeurDomaineDto[];
  messageApplication: { [key: string]: string } = {};
  today = new Date();
  rapportCommentaireLayer: VectorLayer;
  public defaultStyle: olstyle.Style | ((feature, resolution) => olstyle.Style);
  commentaireLayerToQuery: NameOfCommentaireLayer;
  templateToUse: EntityTableTemplate;
  public templatePlanTravailOfficiel: EntityTableTemplate = {
    columns: [
      {
        title: this.languageService.translate.instant('sigt.integration.rapportCommentaires.grid.date'),
        name: 'properties.date'
      },
      {
        title: this.languageService.translate.instant('sigt.integration.rapportCommentaires.grid.auteur'),
        name: 'properties.auteur'
      },
      {
        title: this.languageService.translate.instant('sigt.integration.rapportCommentaires.grid.codeUtilisateur'),
        name: 'properties.CODE_UTILISATEUR'
      },
      {
        title: this.languageService.translate.instant('sigt.integration.rapportCommentaires.grid.courriel'),
        name: 'properties.ADR_COURR_UTILISATEUR'
      },
      {
        title: this.languageService.translate.instant('sigt.integration.rapportCommentaires.grid.typePlan'),
        name: 'properties.TYPE_PLAN'
      },
      {
        title: this.languageService.translate.instant('sigt.integration.rapportCommentaires.grid.longitude'),
        name: 'properties.longitude'
      },
      {
        title: this.languageService.translate.instant('sigt.integration.rapportCommentaires.grid.latitude'),
        name: 'properties.latitude'
      },
      {
        title: this.languageService.translate.instant('sigt.integration.rapportCommentaires.grid.region'),
        name: 'properties.NO_REGION'
      },
      {
        title: this.languageService.translate.instant('sigt.integration.rapportCommentaires.grid.commentaire'),
        name: 'properties.COMMENTAIRE',
        cellClassFunc: () => {
          return { rapportCommentaireCommentColumn: true };
        }
      },
      {
        name: 'action',
        title: '',
        valueAccessor: () => {
          return [
            {
              icon: 'print',
              color: 'accent',
              click: (row: any) => {
                console.log(row);
                this.storageService.set('sigtRapportCommentaire', JSON.stringify(row.properties));
                window.open('/carte/?sa=commentaire');
              }
            }
          ] as EntityTableButton[];
        },
        renderer: EntityTableColumnRenderer.ButtonGroup
      }
    ]
  };
  public templateConsultations: EntityTableTemplate = {
    columns: [
      {
        title: this.languageService.translate.instant('sigt.integration.rapportCommentaires.grid.date'),
        name: 'properties.date'
      },
      {
        title: this.languageService.translate.instant('sigt.integration.rapportCommentaires.grid.auteur'),
        name: 'properties.auteur'
      },
      {
        title: this.languageService.translate.instant('sigt.integration.rapportCommentaires.grid.courriel'),
        name: 'properties.ADR_COURR_UTILISATEUR'
      },
      {
        title: this.languageService.translate.instant('sigt.integration.rapportCommentaires.grid.longitude'),
        name: 'properties.longitude'
      },
      {
        title: this.languageService.translate.instant('sigt.integration.rapportCommentaires.grid.latitude'),
        name: 'properties.latitude'
      },
      {
        title: this.languageService.translate.instant('sigt.integration.rapportCommentaires.grid.region'),
        name: 'properties.NO_REGION'
      },
      {
        title: this.languageService.translate.instant('sigt.integration.rapportCommentaires.grid.typeAvis'),
        name: 'properties.typeAvis'
      },
      {
        title: this.languageService.translate.instant('sigt.integration.rapportCommentaires.grid.objet'),
        name: 'properties.objet'
      },
      {
        title: this.languageService.translate.instant('sigt.integration.rapportCommentaires.grid.commentaire'),
        name: 'properties.COMMENTAIRE',
        cellClassFunc: () => {
          return { rapportCommentaireCommentColumn: true };
        }
      },
      {
        name: 'action',
        title: '',
        valueAccessor: () => {
          return [
            {
              icon: 'print',
              color: 'accent',
              click: (row: any) => {
                console.log(row);
                this.storageService.set('sigtRapportCommentaire', JSON.stringify(row.properties));
                window.open('/carte/?sa=commentaire');
              }
            }
          ] as EntityTableButton[];
        },
        renderer: EntityTableColumnRenderer.ButtonGroup
      }
    ]
  };
  private layerWorkspaceId: string = 'rapportCommentaire';

  get map(): IgoMap {
    return this.mapState.map;
  }

  constructor(
    private configService: ZzzConfigService,
    private formBuilder: UntypedFormBuilder,
    private domainesService: DomainesService,
    private messageService: MessageService,
    private httpClient: HttpClient,
    private dataSourceService: DataSourceService,
    private layerService: LayerService,
    private mapState: MapState,
    private workspaceState: WorkspaceState,
    private cdr: ChangeDetectorRef,
    private storageService: StorageService
  ) { }

  ngOnInit(): void {
    this.baseUrl = this.configService.get().authApiEndPoint;

    this.form = this.formBuilder.group({
      regions: ["", [Validators.required]],
      typePlan: ["", [Validators.required]],
      dateDebut: [""],
      dateFin: [""],
    }, { validators: [this.validerChamps.bind(this)] });

    this.domainesService.obtenirValeurs("TYPE_RAPPORT_COM")
      .subscribe({
        next: (data) => {
          this.listeTypeDePlan = data.valeursDomaine;
          if (this.listeTypeDePlan.length > 0) {
            this.valeurTypePlanDefaut = this.listeTypeDePlan[0].valeur;
            this.form.patchValue({ typePlan: this.valeurTypePlanDefaut });
          }
        },
        error: (error) => {
          console.log("Erreur: " + JSON.stringify(error));
        },
      }
      );

    this.domainesService.obtenirValeurs("REGION_ADMIN")
      .subscribe({
        next: (data) => {
          this.listeRegion = data.valeursDomaine;
        },
        error: (error) => {
          console.log("Erreur: " + JSON.stringify(error));
        },
      }
      );

    this.messageApplication.zeroResults = Constantes.MessageApplicatif.AucunElement_6I;
    this.messageApplication.dateDebutSuperieurFin = Constantes.MessageApplicatif.DateFinSuperieurDateDebut_15E;
    this.rapportCommentaireLayer = <VectorLayer>this.mapState.map.layerController.all.find((l) => l.id === this.layerWorkspaceId);
    if (this.rapportCommentaireLayer) {
      this.supprimerLayer();
    }

    this.form.controls.typePlan.valueChanges.subscribe((value) => {
      if (value.toLowerCase() === 'consultation') {
        this.commentaireLayerToQuery = 'Consultations';
        this.templateToUse = this.templateConsultations;
      } else {
        this.templateToUse = this.templatePlanTravailOfficiel;
        if (value.toLowerCase() === 'plans de travail') {
          this.commentaireLayerToQuery = 'CommentairesPlansTravailPoints';
          this.templateToUse.columns[2].visible = true;
        } else {
          //plans officiels
          this.commentaireLayerToQuery = 'CommentairesPlansOfficielsPoints';
          this.templateToUse.columns[2].visible = false;
        }
      }
      this.storageService.set('sigtRapportCommentaireTypePlan', this.commentaireLayerToQuery);
    });
  }

  rechercher() {
    if (this.rapportCommentaireLayer) {
      this.supprimerLayer();
      setTimeout(() => this.rechercher(), 200);
      return;
    }

    /* #region: initialisation des filtres */
    var dateDebutFilter = '';
    if (this.form.controls.dateDebut.value) {
      var dateDebut = new Date(new Date(this.form.controls.dateDebut.value).setHours(0, 0, 0, 0)).toISOString();
      dateDebutFilter = ' AND DATE_COMMENTAIRE AFTER ' + dateDebut;
    }
    var dateFinFilter = '';
    if (this.form.controls.dateFin.value) {
      var dateFin = new Date(new Date(this.form.controls.dateFin.value).setHours(23, 59, 59, 0)).toISOString();
      dateFinFilter = ' AND DATE_COMMENTAIRE BEFORE ' + dateFin;
    }
    /* #endregion: initialisation des filtres */

    const wfsUrl = this.baseUrl + '/SIGT_COM/wfs';
    this.httpClient.get<{ features: Feature[] }>(wfsUrl
      + `?SERVICE=WFS&REQUEST=GetFeature&VERSION=2.0.0&TYPENAMES=SIGT_COM:${this.commentaireLayerToQuery}
      &SRSNAME=urn:ogc:def:crs:EPSG::3857
      &outputFormat=application/json
      &CQL_FILTER=NO_REGION IN (${this.form.controls.regions.value.map(m => `'${m}'`).join(',')})
       ${dateDebutFilter}${dateFinFilter}`)
      .subscribe((f) => {
        if (f.features.length === 0) {
          this.messageService.info(this.messageApplication.zeroResults);
        } else {
          let features = f.features;
          this.defaultStyle = new olstyle.Style({
            fill: new olstyle.Fill({
              color: [89, 200, 20, 0.8]
            }),
            stroke: new olstyle.Stroke({
              width: 1,
              color: 'black'
            }),
            image: new olstyle.Circle({
              radius: 4,
              fill: new olstyle.Fill({
                color: [89, 200, 20, 0.8]
              }),
              stroke: new olstyle.Stroke({
                width: 1,
                color: 'black'
              })
            })
          });
          this.dataSourceService
            .createAsyncDataSource({
              type: 'vector',
              queryable: false //to avoid showing the toastr-panel
            } as QueryableDataSourceOptions)
            .pipe(take(1))
            .subscribe((dataSource: DataSource) => {
              const olLayer = this.layerService.createLayer({
                id: this.layerWorkspaceId,
                isIgoInternalLayer: true,
                title: this.languageService.translate.instant('sigt.integration.rapportCommentaires.form.title'),
                source: dataSource,
                visible: true,
                workspace: {
                  enabled: true
                },
                style: this.defaultStyle,
                igoStyle: {
                  editable: true, //to show the option to change the style
                }
              }) as VectorLayer;
              const featuresOl = features.map((f) => {
                f.projection = this.map.projectionCode;
                f.properties.auteur = f.properties.PRENOM_UTILISATEUR + ' ' + f.properties.NOM_UTILISATEUR;
                f.properties.date = f.properties.DATE_COMMENTAIRE.split('T')[0];
                f.properties.longitude = f.properties.LONGITUDE;
                f.properties.latitude = f.properties.LATITUDE;
                f.properties.action = {
                  name
                };
                if (this.commentaireLayerToQuery === 'Consultations') {
                  f.properties.objet = f.properties.OBJET_COM_GDF;
                  f.properties.typeAvis = f.properties.TYPE_AVIS_GDF;
                }
                return featureToOl(f, this.map.projectionCode);
              });
              this.rapportCommentaireLayer = olLayer;

              const ol = dataSource.ol as olVectorSource | olClusterSource;
              olLayer.options.workspace = Object.assign({}, olLayer.options.workspace, {
                queryOptions: { tabQuery: false }// to avoid showing the toastr-panel
              } as GeoWorkspaceOptions);
              ol.addFeatures(featuresOl);

              this.map.layerController.add(olLayer);
              this.cdr.detectChanges();
              let workspaceStoreSubscriptionUntilActivated: Subscription = new Subscription();
              workspaceStoreSubscriptionUntilActivated =
                this.workspaceState.store.entities$.pipe(debounceTime(500)).subscribe((_value) => {
                  this.workspaceState.store.all()
                    .filter(f => f.id === this.layerWorkspaceId)
                    .forEach((workspace) => {
                      workspace.meta.tableTemplate.columns = this.templateToUse.columns;
                      this.workspaceState.setActiveWorkspaceById(workspace.id);
                    });
                  workspaceStoreSubscriptionUntilActivated.unsubscribe();
                });
            });
        }
      });
  }

  initialiserChamps() {
    this.form.patchValue({
      regions: '',
      dateDebut: '',
      dateFin: ''
    });
    this.form.get('regions').setErrors(null, { emitEvent: false });
  }

  validerChamps(controlGroup: FormGroup): { [key: string]: any } | null {
    const regions = controlGroup.controls["regions"].value;
    if (regions === null || regions?.length === 0) {
      return { notValid: true };
    }
    const champDateDebut = controlGroup.controls["dateDebut"].value;
    const champDateFin = controlGroup.controls["dateFin"].value;
    if (champDateDebut && champDateFin) {
      const dateDebut = new Date(champDateDebut);
      dateDebut.setHours(0, 0, 0, 0);

      const dateFin = new Date(champDateFin);
      dateFin.setHours(0, 0, 0, 0);

      if (dateDebut > dateFin)
        return {
          dateDebutFinErreur: {
            message: this.messageApplication.dateDebutSuperieurFin.replace('{0}', '')
          }
        };
    }
    return null;
  }

  supprimerLayer() {
    this.map.layerController.remove(this.rapportCommentaireLayer);
    this.rapportCommentaireLayer = undefined;
  }
}
